import React, { useMemo } from "react"
import { Flex, Text } from "@chakra-ui/react"
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table"
import { GuestSummary } from "sharedTypes"
import { isColumnSortable, type ColumnConfig, type ColumnId } from "./columnsConfig"
import { useColumnVisibilityContext } from "../../ColumnVisibilityProvider"
import {
  type ExtendedGuestSummary,
  getColumnAccessor,
} from "./helpers"
import ColumnCell from "./ColumnCell"
import SelectAllCheckbox from "./SelectAllCheckbox"

type UseTableColumnsProps = {
  availableColumnConfigs: Record<string, ColumnConfig>;
}

type UseTableColumnsReturn = {
  tableColumns: ColumnDef<ExtendedGuestSummary, unknown>[];
}

const columnHelper = createColumnHelper<GuestSummary>()

const useTableColumns = ({
  availableColumnConfigs,
}: UseTableColumnsProps): UseTableColumnsReturn => {
  const { columnVisibility } = useColumnVisibilityContext()

  const tableColumns = useMemo(() => {
    const columns: ColumnDef<ExtendedGuestSummary, unknown>[] = []

    Object.entries(availableColumnConfigs).forEach(([columnId, config]) => {
      const isVisible = columnId === "name" || columnVisibility?.[columnId]?.isVisible
      if (!isVisible) return

      const canSort = isColumnSortable(columnId as ColumnId)

      columns.push(
        columnHelper.accessor(getColumnAccessor(columnId), {
          id: columnId,
          header: columnId === "name" ? ({ table }) => {
            const meta = table.options.meta!
            if (!meta.multipleSelect) return config.label

            return (
              <Flex align="center">
                <Flex
                  align="center"
                  justify="center"
                  position="relative"
                  mr={2}
                  w={8}
                  h={8}
                  boxSizing="content-box"
                  onClick={(e) => e.stopPropagation()}
                >
                  <SelectAllCheckbox
                    allSelected={meta.allSelected}
                    onToggle={meta.toggleSelectAll}
                  />
                </Flex>
                <Text>{config.label}</Text>
              </Flex>
            )
          } : config.label,
          size: config.defaultWidth,
          enableHiding: columnId !== "name",
          enableSorting: canSort,
          cell: ({ row, getValue, table }) => (
            <ColumnCell
              columnId={columnId}
              row={row}
              getValue={getValue}
              table={table}
            />
          ),
          meta: { icon: config.icon },
        }),
      )
    })

    return columns
  }, [
    availableColumnConfigs,
    columnVisibility,
  ])

  return {
    tableColumns,
  }
}

export default useTableColumns
